<template>
    <div class="projects-table" role="region" :aria-busy="loading.toString()" aria-labelledby="projects-heading">
        <v-snackbar top v-model="notification.show" :color="notification.color" role="status" aria-live="polite" aria-atomic="true">
            <v-icon>{{ notification.icon }}</v-icon>
            <span class="font-weight-bold pl-2"> {{ notification.message }} </span>
        </v-snackbar>

        <EditProject :editDialog="editProjectDialog" :all_collections="researches" :project_data="selectedProject" @close="editProjectDialog = false" @renewData="renewData"></EditProject>
        <DeleteDialog
            :deleteDialog="deleteDialog"
            :selectedDeleteItem="selectedDeleteItem ? selectedDeleteItem.project : null"
            deleteLabel="Project"
            :loading="deleteLoading"
            @delete="deleteProject"
            @close="deleteDialog = false"></DeleteDialog>
        <AddProjectsDialog :all_collections="researches" :projectsDialog="projectsDialog" @created="$emit('renewData')" @close="projectsDialog = false"></AddProjectsDialog>
        <ProjectsBanner
            v-if="projects.length < 1"
            class="mt-5 mb-10"
            @showAddDialog="
                {
                    projectsDialog = true
                }
            "></ProjectsBanner>
        <v-card v-else class="rounded_card quaroshadow px-5 py-3 my-8 mb-10">
            <v-row no-gutters>
                <v-sheet color="primary" max-height="200" class="pa-12 quaroshadow rounded_card" dark top id="tablesheet" elevation="6">
                    <div class="headingsheet2">
                        <v-icon :light="this.$vuetify.theme.dark ? true : false" id="sheeticon" large>mdi-folder-table</v-icon>
                    </div>
                </v-sheet>
                <v-card-title id="heading3" style="display: block" class="pt-0"
                    ><h1 id="projects-heading" style="font-size: 1.5em; font-weight: 300">All Projects</h1>
                    <div>
                        <v-chip small>{{ statusText }}</v-chip>
                    </div>
                </v-card-title>
                <v-spacer></v-spacer>
                <v-text-field
                    v-model="projectsSearch"
                    style="max-width: 400px"
                    rounded
                    filled
                    dense
                    append-icon="mdi-magnify"
                    label="Search for project"
                    single-line
                    hide-details
                    id="kwsearch"
                    :aria-describedby="'projects-search-hint projects-count'"
                    :aria-controls="'projtable'"></v-text-field>
                <p id="projects-search-hint" class="sr-only">Type to filter the list. Results update as you type.</p>
                <p id="projects-count" class="sr-only" aria-live="polite">{{ statusText }}</p>
            </v-row>

            <v-tooltip v-if="$helpers.isActionAllowed(30, user_rights)" top>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" absolute right fab dark small @click="projectsDialog = true" color="primary" class="fabicon" aria-label="Create project">
                        <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                </template>
                <span>create a project</span>
            </v-tooltip>

            <v-data-table
                id="projtable"
                :headers="headers"
                :loading="loading"
                :items="projects"
                :single-expand="singleExpand"
                :search.sync="projectsSearch"
                :expanded.sync="expanded"
                :sort-by.sync="projects_sort_by"
                :sort-desc.sync="projects_sort_desc"
                :items-per-page.sync="projects_items_per_page"
                item-key="_id"
                show-expand
                expand-icon="mdi-chevron-down"
                class="row-pointer mt-2">
                <!-- header slot -->
                // ...existing code...
                <template v-for="(header_entry, index) in headers" v-slot:[`header.${header_entry.value}`]="{ header }">
                    <span :key="index">
                        <span v-bind:style="{ width: header.width }">
                            <!-- Sortierbare Header als Buttons -->
                            <template v-if="header.sortable">
                                <v-tooltip v-if="header.tooltip" color="primary" top max-width="250px" open-on-focus>
                                    <template #activator="{ on, attrs }">
                                        <v-btn v-bind="attrs" v-on="on" small text class="th-btn th-btn--header mr-1" :aria-describedby="`tt-${header.value}`">
                                            <span class="th-text">{{ header.text }}</span>
                                            <v-icon x-small aria-hidden="true" class="ml-1">mdi-help</v-icon>
                                        </v-btn>
                                    </template>
                                    <span :id="`tt-${header.value}`" role="tooltip">{{ header.tooltip }}</span>
                                </v-tooltip>
                                <v-btn v-else small text class="th-btn th-btn--header">
                                    <span class="th-text">{{ header.text }}</span>
                                </v-btn>
                                <!-- ARIA Sort Status nur für sortable -->
                                <span class="sr-only" aria-live="polite">
                                    <span v-if="projects_sort_by && projects_sort_by[0] === header.value && !(projects_sort_desc && projects_sort_desc[0])"> (sorted in ascending order)</span>
                                    <span v-else-if="projects_sort_by && projects_sort_by[0] === header.value && projects_sort_desc && projects_sort_desc[0]"> (sorted in descending order)</span>
                                </span>
                            </template>

                            <!-- Nicht sortierbare Header: nur Text -->
                            <template v-else>
                                <span class="th-text">{{ header.text }}</span>
                            </template>
                        </span>
                    </span>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="px-0">
                        <SimpleResearchTable
                            @renewData="renewData"
                            @removeCollection="removeCollectionFromTable"
                            @openShareMenu="openShareMenu"
                            @initChangeNameDialog="initChangeNameDialog"
                            :researches="getResearches(item)"
                            :autofocus="true"></SimpleResearchTable>
                    </td>
                </template>
                <template v-slot:item.description="{ item }">
                    <div style="max-width: 800px" class="py-2">{{ item.description }}</div>
                </template>
                <template v-slot:item.project="{ item }">
                    <v-list-item two-line>
                        <v-list-item-content>
                            <v-list-item-title class="project-title mb-1">{{ item.project }}</v-list-item-title>
                            <v-list-item-subtitle class="updated-subtitle" style="display: flex"
                                ><v-icon x-small class="pr-1">mdi-clock-outline</v-icon>
                                <span>
                                    Updated
                                    {{ item.updated_text }}
                                </span>
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </template>
                <template v-slot:item.owner="{ item }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-avatar v-on="on" color="primary" size="30" class="pr-0 avat">
                                <span class="white--text">{{ getInitials(item.owner_name) }}</span>
                            </v-avatar>
                        </template>
                        <span>{{ item.owner_name }}</span>
                    </v-tooltip>
                </template>
                <template v-slot:item.action="{ item }">
                    <v-menu rounded="lg" color="primary" transition="slide-y-transition" bottom v-if="item.status != 'creating' && item.status != 'updating'">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                class="ml-auto"
                                v-if="$helpers.isActionAllowed(32, user_rights) || $helpers.isActionAllowed(31, user_rights)"
                                id="cogbtn"
                                icon
                                v-on="on"
                                v-bind="attrs"
                                aria-haspopup="menu"
                                :aria-label="`More actions for ${item.project}`">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list rounded dense>
                            <v-list-item v-if="item.status != 'creating' && item.status != 'updating'" @click="downloadProjectCollections(item)">
                                <v-icon small class="mr-2">mdi-cloud-download</v-icon><v-list-item-title>Download</v-list-item-title>
                            </v-list-item>
                            <!-- <v-list-item
                  v-if="item.status != 'creating' && item.status != 'updating' && $helpers.isActionAllowed(4, user_rights)"
                  @click="openShareMenu(item)">
                  <v-icon small class="mr-2">mdi-share-variant</v-icon><v-list-item-title>Share</v-list-item-title>
              </v-list-item> -->
                            <v-list-item v-if="item.status != 'creating' && item.status != 'updating' && $helpers.isActionAllowed(32, user_rights)" @click="showEditProjectDialog(item)">
                                <v-icon small class="mr-2">mdi-pencil</v-icon><v-list-item-title>Edit</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="item.status != 'creating' && item.status != 'updating' && $helpers.isActionAllowed(31, user_rights)" @click="showDeleteDialog(item)">
                                <v-icon small class="mr-2">mdi-delete</v-icon><v-list-item-title>Delete</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:item.lists="{ item }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn tabindex="-1" v-on="on" v-bind="attrs" small icon class="mr-4" :aria-label="`${item.lists.length} assigned collections`">
                                <v-icon small>mdi-file-multiple-outline</v-icon>{{ item.lists.length }}
                            </v-btn>
                        </template>
                        <span>{{ item.lists.length }} assigned collections</span>
                    </v-tooltip>
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn tabindex="-1" v-on="on" v-bind="attrs" small icon class="mr-4" :aria-label="`${item.shared_count} shared collections`">
                                <v-icon small>mdi-share-variant</v-icon>{{ item.shared_count }}
                            </v-btn>
                        </template>
                        <span>{{ item.shared_count }} shared collections</span>
                    </v-tooltip>
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn tabindex="-1" v-on="on" v-bind="attrs" small icon :aria-label="`${item.comments_count} comments`">
                                <v-icon small>mdi-comment-text-outline</v-icon>{{ item.comments_count }}
                            </v-btn>
                        </template>
                        <span>{{ item.comments_count }} comments</span>
                    </v-tooltip>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
    import ProjectsBanner from "./ProjectsBanner"
    import SimpleResearchTable from "./SimpleResearchTable"
    import EditProject from "./EditProject"
    import DeleteDialog from "../dialogs/DeleteDialog"
    import AddProjectsDialog from "./AddProjects"

    export default {
        name: "ProjectsTable",
        props: {
            researches: Array,
            projects: Array,
            loading: Boolean
        },
        components: {
            ProjectsBanner,
            SimpleResearchTable,
            EditProject,
            DeleteDialog,
            AddProjectsDialog
        },
        data() {
            return {
                resultsCount: 0,
                projects_sort_desc: [true],
                projects_sort_by: ["count"],
                projects_items_per_page: 10,
                projectsDialog: false,
                notification: { show: false },
                deleteLoading: false,
                selectedDeleteItem: null,
                deleteDialog: false,
                selectedProject: { project: "", description: "" },
                editProjectDialog: false,
                expanded: [],
                singleExpand: true,
                headers: [
                    { text: "", value: "data-table-expand", sortable: false },
                    {
                        text: "Project",
                        align: "start",
                        sortable: true,
                        value: "project"
                    },
                    { text: "", sortable: false, value: "lists", width: 175 },
                    { text: "Description", value: "description", sortable: true, width: 800 },
                    { text: "Owner", value: "owner", sortable: true },
                    {
                        sortable: false,
                        text: " ",
                        value: "action",
                        width: 50,
                        align: "end"
                    }
                ],
                projectsSearch: null,
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH)
            }
        },

        watch: {
            projectsSearch(val) {
                this.updateResultsCount(val)
            },
            projects() {
                this.updateResultsCount(this.projectsSearch)
            },
            projects_sort_desc() {
                console.log(this.projects_sort_desc)
                this.user_settings.collections_projects_sort.sort = this.projects_sort_desc
                this.user_settings.collections_projects_sort.column = this.projects_sort_by

                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            projects_sort_by() {
                console.log(this.projects_sort_by)
                // this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            projects_items_per_page() {
                console.log(this.projects_items_per_page)
                this.user_settings.collections_projects_rowsperpage = this.projects_items_per_page

                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            }
        },
        methods: {
            updateResultsCount(search) {
                const list = Array.isArray(this.projects) ? this.projects : []
                if (!search) {
                    this.resultsCount = list.length
                    return
                }
                const s = String(search).toLowerCase()
                this.resultsCount = list.filter((p) => {
                    const name = (p.project || "").toLowerCase()
                    const desc = (p.description || "").toLowerCase()
                    return name.includes(s) || desc.includes(s)
                }).length
            },
            initChangeNameDialog(item) {
                this.$emit("initChangeNameDialog", item)
            },
            openShareMenu(item) {
                this.$emit("openShareMenu", item)
            },
            removeCollectionFromTable(list_id, project) {
                this.$emit("removeCollection", list_id, project)
            },
            getInitials(name) {
                let splittedName = name.split(" ")
                let initials = ""
                splittedName.forEach((namepart) => {
                    let firstletter = namepart.substring(0, 1)
                    initials += firstletter
                })
                return initials
            },
            renewData() {
                this.$emit("renewData")
            },
            showDeleteDialog(item) {
                this.selectedDeleteItem = item
                this.deleteDialog = true
            },
            showEditProjectDialog(item) {
                console.log(item)
                this.selectedProject = JSON.parse(JSON.stringify(item))
                this.editProjectDialog = true
            },
            getResearches(project) {
                if (this.expanded.indexOf(project) > -1) {
                    console.log(this.researches)
                    var project_researches = this.researches.filter((research) => project.lists.indexOf(research._id) > -1)
                    console.log(project_researches)
                    return project_researches
                }
            },
            getMonthNumber(monthName) {
                const monthMap = {
                    january: 1,
                    february: 2,
                    march: 3,
                    april: 4,
                    may: 5,
                    june: 6,
                    july: 7,
                    august: 8,
                    september: 9,
                    october: 10,
                    november: 11,
                    december: 12
                }
                if (!monthName) return 0
                return monthMap[String(monthName).toLowerCase()] || Number(monthName) || 0
            },
            buildProjectExportRow(keywordData, projectName, collectionName) {
                let annualsv = 0
                const row = {
                    project: projectName,
                    collection: collectionName,
                    keyword: keywordData.keyword || "",
                    adwords_cpc: keywordData.adwords_cpc,
                    adwords_cpc_low: keywordData.adwords_cpc_low,
                    adwords_cpc_high: keywordData.adwords_cpc_high,
                    adwords_competition: keywordData.adwords_competition,
                    keyword_difficulty: keywordData.keyword_difficulty,
                    serp_items: keywordData.serp_items,
                    season: keywordData.season,
                    categories: keywordData.categories || null,
                    cats: keywordData.categories_edited || null,
                    active_cat_index: keywordData.active_cat_index ? keywordData.active_cat_index : 0,
                    customs: keywordData.customs || {},
                    search_volumes: []
                }

                const volumes = Array.isArray(keywordData.searchvolume) ? keywordData.searchvolume : []
                for (let i = 0; i < volumes.length; i++) {
                    const volume = parseInt(volumes[i].volume) || 0
                    const year = Number(volumes[i].year) || 0
                    const month = this.getMonthNumber(volumes[i].month)
                    annualsv += i < 12 ? volume : 0

                    row.search_volumes.push({
                        count: volume,
                        year: year,
                        month: month
                    })

                    if (year && month) {
                        const svname = month < 10 ? `${year}0${month}` : `${year}${month}`
                        row[svname] = volume
                    }
                }

                row.annualsv = annualsv
                row.avgsv = keywordData.avgsv !== undefined && keywordData.avgsv !== null ? keywordData.avgsv : parseInt(annualsv / 12)

                return row
            },
            normalizeProjectExportRowForCsv(row) {
                const normalized = { ...row }
                if (Array.isArray(normalized.cats) && normalized.cats.length && Array.isArray(normalized.cats[0]) && normalized.cats[0].length) {
                    const activeIndex = Number.isInteger(normalized.active_cat_index) ? normalized.active_cat_index : 0
                    const activePath = Array.isArray(normalized.cats[activeIndex]) && normalized.cats[activeIndex].length ? normalized.cats[activeIndex] : normalized.cats[0]
                    const lastCat = activePath[activePath.length - 1]
                    normalized.cats = lastCat && lastCat.category_name ? lastCat.category_name : null
                } else {
                    normalized.cats = null
                }

                normalized.keyword_difficulty =
                    normalized.keyword_difficulty && normalized.keyword_difficulty.difficulty ? normalized.keyword_difficulty.difficulty : normalized.keyword_difficulty || null
                normalized.season = normalized.season && normalized.season.season ? normalized.season.season : normalized.season || null

                if (Array.isArray(normalized.serp_items) && normalized.serp_items.length) {
                    let serpString = ""
                    normalized.serp_items.map((entry, i) => {
                        const value = entry && entry.value !== undefined ? entry.value : entry
                        serpString += `${value !== undefined && value !== null ? value : ""}${i == normalized.serp_items.length - 1 ? "" : ","}`
                    })
                    normalized.serp_items = serpString
                } else if (typeof normalized.serp_items === "string") {
                    normalized.serp_items = normalized.serp_items
                } else {
                    normalized.serp_items = null
                }

                return normalized
            },
            mergeExportFields(targetFields, incomingFields) {
                const existingValues = new Set(targetFields.map((f) => f.value))
                ;(incomingFields || []).forEach((f) => {
                    if (!f || !f.value) return
                    if (!existingValues.has(f.value)) {
                        targetFields.push(f)
                        existingValues.add(f.value)
                    }
                })
            },
            async downloadProjectCollections(project) {
                try {
                    const projectResearches = (this.researches || []).filter((research) => (project.lists || []).indexOf(research._id) > -1)
                    if (!projectResearches.length) {
                        this.notification = this.$helpers.createNotification("This project has no collections to export.", "mdi-alert-circle-outline", "errorred")
                        return
                    }

                    const responses = await Promise.all(
                        projectResearches.map((researchItem) =>
                            this.$http.post(
                                process.env.VUE_APP_APIURL + "/research/",
                                {
                                    research: researchItem.listname,
                                    svminimum: 0,
                                    company_id: localStorage.company_id
                                },
                                { headers: { Authorization: this.auth } }
                            )
                        )
                    )

                    const rows = []
                    const columns = [
                        { label: "Project", value: "project" },
                        { label: "Collection", value: "collection" }
                    ]

                    responses.forEach((resp, idx) => {
                        const collectionName = projectResearches[idx].listname
                        const keywords = resp.body && resp.body.keywordResponse && Array.isArray(resp.body.keywordResponse.keywords) ? resp.body.keywordResponse.keywords : []
                        const listHeaders =
                            resp.body &&
                            resp.body.listResponse &&
                            resp.body.listResponse[0] &&
                            resp.body.listResponse[0].mdata &&
                            resp.body.listResponse[0].mdata.headers &&
                            Array.isArray(resp.body.listResponse[0].mdata.headers.modifiedHeaders)
                                ? resp.body.listResponse[0].mdata.headers.modifiedHeaders
                                : []

                        const collectionRows = keywords.map((kw) => this.buildProjectExportRow(kw, project.project, collectionName))
                        rows.push(...collectionRows)

                        if (listHeaders.length) {
                            let collectionFields = this.$helpers.deleteDownloadColumns(listHeaders)
                            const foundCustomHeaders = listHeaders.find((x) => x.custom == true)
                            if (foundCustomHeaders) collectionFields = this.$helpers.addCustoms(collectionRows, collectionFields)
                            this.mergeExportFields(columns, collectionFields)
                        }
                    })

                    if (!rows.length) {
                        this.notification = this.$helpers.createNotification("No keyword data available for export.", "mdi-alert-circle-outline", "errorred")
                        return
                    }

                    if (columns.length <= 2) {
                        this.mergeExportFields(columns, [
                            { label: "Keyword", value: "keyword" },
                            { label: "Google Searches YEARLY", value: "annualsv" },
                            { label: "Google Searches AVG MONTHLY", value: "avgsv" },
                            { label: "CPC", value: "adwords_cpc" },
                            { label: "Adwords Competition", value: "adwords_competition" }
                        ])
                    }

                    const csvRows = this.$helpers.deleteDownloadAttributes(rows.map((row) => this.normalizeProjectExportRowForCsv(row)))

                    const exportResponse = await this.$http.post(process.env.VUE_APP_APIURL + "/research/json2csv", { data: csvRows, columns: columns }, { headers: { Authorization: this.auth } })

                    window.location = process.env.VUE_APP_APIURL + "/" + exportResponse.body
                    this.notification = this.$helpers.createNotification("Project export is ready.", "mdi-check-circle-outline", "success")
                } catch (error) {
                    console.log(error)
                    this.notification = this.$helpers.createNotification("Project export failed. Please try again.", "mdi-alert-circle-outline", "errorred")
                }
            },
            deleteProject() {
                console.log(this.selectedDeleteItem)
                this.deleteLoading = true
                this.$http
                    .post(
                        process.env.VUE_APP_APIURL + "/projects/delete",
                        {
                            company_id: localStorage.company_id,
                            project_id: this.selectedDeleteItem._id
                        },
                        { headers: { Authorization: this.auth } }
                    )
                    .then(function (response) {
                        console.log(response)
                        if (response.body.deleted) {
                            this.notification = this.$helpers.createNotification("The Project has been deleted successfully!", "mdi-check-circle-outline", "success")
                            this.$emit("renewData")
                            this.deleteDialog = false
                        } else {
                            this.notification = this.$helpers.createNotification("Something went wrong! Please try again or contact the support.", "mdi-alert", "errorred")
                        }
                        this.deleteLoading = false
                    })
                    .catch((err) => {
                        console.log(err)
                        this.deleteLoading = false
                        this.notification = this.$helpers.createNotification("Something went wrong! Please try again or contact the support.", "mdi-alert", "errorred")
                    })
            }
        },
        computed: {
            statusText() {
                if (!this.projectsSearch) return `${(this.projects || []).length} Projects`
                const n = this.resultsCount
                return `${n} Project${n === 1 ? "" : "s"} for “${this.projectsSearch}”.`
            },
            user_rights() {
                return this.$store.state.user_rights
            },
            initials() {
                let splittedName = this.decryptedName.split(" ")
                let initials = ""
                splittedName.forEach((namepart) => {
                    let firstletter = namepart.substring(0, 1)
                    initials += firstletter
                })
                return initials
            },
            decryptedName() {
                if (localStorage.username.length > 0) {
                    return this.CryptoJS.AES.decrypt(localStorage.username, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                } else {
                    return ""
                }
            },
            decryptedId() {
                if (localStorage.id) {
                    return this.CryptoJS.AES.decrypt(localStorage.id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                } else {
                    return ""
                }
            },
            user_settings() {
                return this.$store.state.user_settings
            }
        },
        mounted() {
            // this.fetchProjects()
            this.projects_sort_desc = this.user_settings.collections_projects_sort.sort
            this.projects_sort_by = this.user_settings.collections_projects_sort.column
            this.projects_items_per_page = this.user_settings.collections_projects_rowsperpage
            this.updateResultsCount(this.projectsSearch)
        }
    }
</script>

<style>
    .updated-subtitle {
        font-size: 0.8em;
    }
    .bold {
        font-weight: bold;
    }
    .welcommessage {
        font-family: Nunito;
        font-weight: 100;
        padding-bottom: 0px;
    }
    #welcomecard {
        padding: 15px;
        color: white;
        background: linear-gradient(56deg, rgba(28, 237, 178, 1) 0%, rgba(0, 159, 138, 1) 0%, rgb(28, 237, 178) 100%);
    }
    .welcometitle {
        font-family: Nunito;
        font-weight: 100;
        text-align: center;
    }
    .subtitle {
        text-align: center;
        font-family: Nunito;
    }
    .chiptext {
        font-weight: bold;
        color: var(--v-primary-base);
    }
    .sitoquaro div {
        display: inline;
        padding: 10px;
    }
    .sitoquaro {
        text-align: center;
        margin-left: auto;
    }
    #tablesheet {
        margin-left: 5px;
        margin-top: -30px;
        width: 60px;
        height: 60px;
    }
    #heading3 {
        font-size: 0.9rem;
        font-weight: 200 !important;
        color: rgb(105, 105, 105);
    }
    .headingsheet2 {
        margin-top: -15px;
    }
    #sheeticon {
        margin-left: -25px;
        margin-top: -25px;
        margin: auto !important;
        padding-bottom: 50% !important;
        width: 50% !important;
    }
    .project-title {
        font-weight: 100;
        font-size: 1.2em;
    }

    #projtable table > tbody > tr > td:last-child {
        background: var(--v-keywordcol-base) !important;
        border-left: 2px solid var(--v-primary-base);
    }
    #projtable table > tbody > tr > td:last-child,
    #projtable table > thead > tr > th:last-child {
        position: sticky !important;
        position: -webkit-sticky !important;
        right: 0 !important;
    }
    .avat {
        background: linear-gradient(56deg, var(--v-left_nav_gradient1-base) 0%, var(--v-left_nav_gradient3-base) 100%);
        font-size: 0.9em;
    }
</style>
